import { useTranslation } from '@silospot/i18n/react';
import { toast } from '@silospot/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ReactElement, ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Badge, Btn, Card, CardHead, PageHeader } from '../../components/cld';
import { CloudflareCard } from '../../components/cld/cloudflare-card';
import { GoalsSection } from '../../components/cld/goals-section';
import { GoogleAdsCard } from '../../components/cld/google-ads-card';
import { TrackerInstall } from '../../components/cld/tracker-install';
import { api } from '../../lib/api';
import { useProjectStore } from '../../stores/project-store';

/** A section whose backend isn't wired yet — shown locked, never faked. */
function LockedSection({ title }: { title: string }): ReactElement {
  const { t } = useTranslation('shell');
  return (
    <Card className="opacity-80">
      <CardHead title={title} />
      <div className="flex items-center gap-2 text-[13px] text-text-3">
        <span className="grid h-5 w-5 place-items-center rounded-full bg-neutral-bg text-[11px]">
          —
        </span>
        {t('soon')}
      </div>
    </Card>
  );
}

export function SettingsPage(): ReactElement {
  const { t } = useTranslation('pages');
  const { t: ts } = useTranslation('shell');
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setCurrentProjectId = useProjectStore((s) => s.setCurrentProjectId);

  const projectQ = useQuery({
    queryKey: ['cld-settings-project', id],
    queryFn: () => api.getProject(id),
    enabled: !!id,
  });
  const usageQ = useQuery({ queryKey: ['cld-usage'], queryFn: () => api.getUsage() });
  const leaveAfterRemoval = async () => {
    // Drop the just-removed site as the current one (the shell reconciles it to
    // the first remaining site), and refresh the project list BEFORE leaving so
    // the home redirect lands on a real site — never the removed one.
    setCurrentProjectId(null);
    await Promise.all([
      qc.refetchQueries({ queryKey: ['projects'] }),
      qc.invalidateQueries({ queryKey: ['onboarding'] }),
    ]);
    navigate('/dashboard');
  };
  const del = useMutation({
    mutationFn: () => api.deleteProject(id),
    onSuccess: async () => {
      toast.success(t('settings.deleteSite'));
      await leaveAfterRemoval();
    },
  });
  // Permanent delete (R6): skip the grace window — the data is purged, not kept.
  const purge = useMutation({
    mutationFn: () => api.purgeProject(id),
    onSuccess: async () => {
      toast.success(t('settings.purgeDone'));
      await leaveAfterRemoval();
    },
    onError: () => toast.error(t('settings.purgeError')),
  });

  const project = projectQ.data?.project;

  return (
    <div>
      <PageHeader title={t('settings.title')} sub={project?.primaryDomain} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* 1 — Tracker install (Palier E: choice + 3 methods, gated by flags) */}
        <Card className="lg:col-span-2">
          <CardHead title={t('settings.tracker')} sub={t('settings.trackerDesc')} />
          {project ? (
            <TrackerInstall
              projectId={id}
              projectDomain={project.primaryDomain.replace(/^https?:\/\//, '')}
              publicKey={project.publicKey}
              secretKey={project.secretKey}
              embedded
              onDone={() => toast.success(t('ti.installedOk'))}
            />
          ) : (
            <div className="h-24 animate-pulse rounded-xl bg-neutral-bg" />
          )}
        </Card>

        {/* 2 — Domains */}
        <Card>
          <CardHead title={t('settings.domains')} />
          <Field label={ts('topbar.selectSite')}>{project?.primaryDomain ?? '—'}</Field>
          {(project?.additionalDomains ?? []).map((d) => (
            <Field key={d} label="+">
              {d}
            </Field>
          ))}
        </Card>

        {/* 5 — Plan */}
        <Card>
          <CardHead title={t('settings.plan')} />
          <Field label={t('settings.plan')}>
            <Badge tone="blue">{usageQ.data?.plan ?? '—'}</Badge>
          </Field>
          {usageQ.data ? (
            <Field label={t('audience.visits')}>
              {usageQ.data.used.toLocaleString('fr-FR')} /{' '}
              {usageQ.data.limit.toLocaleString('fr-FR')}
            </Field>
          ) : null}
        </Card>

        {/* Google Ads integration (Palier C, read-only) */}
        <GoogleAdsCard projectId={id} />

        {/* Cloudflare integration (Palier D, edge blocking) */}
        <CloudflareCard projectId={id} />

        {/* Conversion goals (Palier B) */}
        <GoalsSection projectId={id} />

        {/* 3, 4, 6 — integration-gated */}
        <LockedSection title={t('settings.team')} />
        <LockedSection title={t('settings.notifications')} />
        <LockedSection title={t('settings.billing')} />

        {/* 7 — Danger zone */}
        <Card className="lg:col-span-2 border-[#f3c9c9]">
          <CardHead title={t('settings.danger')} sub={t('settings.deleteWarning')} />
          <div className="flex flex-wrap items-center gap-2">
            <Btn
              danger
              disabled={del.isPending}
              onClick={() => {
                if (window.confirm(t('settings.deleteWarning'))) del.mutate();
              }}
            >
              {t('settings.deleteSite')}
            </Btn>
            <Btn
              danger
              disabled={purge.isPending}
              onClick={() => {
                if (window.confirm(t('settings.purgeConfirm'))) purge.mutate();
              }}
            >
              {t('settings.purgeSite')}
            </Btn>
          </div>
          <p className="mt-2 text-[12px] text-text-3">{t('settings.purgeNote')}</p>
        </Card>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: ReactNode; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-center justify-between gap-3 border-b border-[#f1f3f6] py-2.5 text-[13px] last:border-0">
      <span className="text-text-2">{label}</span>
      <span className="text-right font-medium text-text">{children}</span>
    </div>
  );
}
